import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';

/** @param {string | { ticketId: string, silent?: boolean }} arg */
function normalizeGetTicketMessagesArg(arg) {
  if (arg == null || arg === '') return { ticketId: '', silent: false };
  if (typeof arg === 'object' && arg !== null && arg.ticketId != null) {
    return { ticketId: String(arg.ticketId), silent: Boolean(arg.silent) };
  }
  return { ticketId: String(arg), silent: false };
}

export const getTicketMessagesById = createAsyncThunk(
  'ticket/getTicketMessagesById',
  async (arg, { getState, rejectWithValue }) => {
    const { ticketId } = normalizeGetTicketMessagesArg(arg);
    if (!ticketId) return rejectWithValue('Missing ticketId');
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;

      const response = await axios.get(
        `${ROOT_URL}/ticket/ticket-message/${ticketId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  messages: [],
  lastFetchedTicketId: null,
  /** Messages keyed by ticket id for revisit after viewing another ticket */
  messagesByTicketId: {},
};

const getTicketMessagesByIdSlice = createSlice({
  name: 'getTicketMessagesById',
  initialState,
  reducers: {
    resetMessagesState: (state) => {
      state.loading = false;
      state.error = null;
      state.messages = [];
      state.lastFetchedTicketId = null;
      state.messagesByTicketId = {};
    },
    optimisticAdd: (state, action) => {
      state.messages.push(action.payload);
      const id = state.lastFetchedTicketId;
      if (id != null) {
        const key = String(id);
        state.messagesByTicketId[key] = [...state.messages];
      }
    },
    optimisticRemove: (state, action) => {
      state.messages = state.messages.filter(
        (message) => message._id !== action.payload
      );
      const id = state.lastFetchedTicketId;
      if (
        id != null &&
        Object.prototype.hasOwnProperty.call(state.messagesByTicketId, String(id))
      ) {
        state.messagesByTicketId[String(id)] = [...state.messages];
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTicketMessagesById.pending, (state, action) => {
        const { ticketId: idStr, silent } = normalizeGetTicketMessagesArg(
          action.meta.arg
        );
        const hasCached =
          idStr &&
          Object.prototype.hasOwnProperty.call(state.messagesByTicketId, idStr);
        const sameInMemory =
          idStr &&
          state.lastFetchedTicketId != null &&
          String(state.lastFetchedTicketId) === idStr;
        if (hasCached) {
          const snapshot = [...state.messagesByTicketId[idStr]];
          state.messages = snapshot;
          state.messagesByTicketId[idStr] = snapshot;
          state.lastFetchedTicketId = idStr;
        } else if (!silent && !sameInMemory) {
          state.messages = [];
        }
        // Background refreshes must not flash message-area loading UI.
        state.loading = silent || hasCached || sameInMemory ? false : true;
        state.error = null;
      })
      .addCase(getTicketMessagesById.fulfilled, (state, action) => {
        state.loading = false;
        const next =
          action.payload?.data != null ? action.payload.data : [];
        const arr = Array.isArray(next) ? next : [];
        state.messages = arr;
        const { ticketId: idStr } = normalizeGetTicketMessagesArg(
          action.meta.arg
        );
        if (idStr) {
          state.messagesByTicketId[idStr] = arr;
          state.lastFetchedTicketId = idStr;
        }
      })
      .addCase(getTicketMessagesById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetMessagesState, optimisticAdd, optimisticRemove } = getTicketMessagesByIdSlice.actions;
export default getTicketMessagesByIdSlice.reducer;
